import { BusinessObjectBase } from './base';
import { processInstanceSchema } from '@schemas/business-objects/process-instance';
import type { PendingStateUpdate } from './pending-state-update';
import type { ProcessDesign } from './process-design';
import type { ProcessInstanceHistory } from './process-instance-history';
import type { Tenant } from './tenant';
import type { Token } from './token';
import type { Id, Json, ProcessInstanceStatus } from '../base-types';
import type { ActivityMapping } from './activity-mapping';
import type { LatestProcessDesign } from './latest-process-design';
import type { MessageListener } from './message-listener';
import type { ServiceTask } from './service-task';
import type { Timer } from './timer';
import type { TokenHistory } from './token-history';
import type { TokenLink } from './token-link';
import type { UserTask } from './user-task';

export interface ProcessInstanceData {
  readonly id: Id;
  readonly referenceNumber?: number;
  readonly name?: string;
  readonly status?: ProcessInstanceStatus;
  readonly data?: Json;
  readonly errorMessage?: string;
  readonly errorCode?: string;
  readonly claimedBy?: string;
  readonly claimedAt?: Date;
  readonly completedAt?: Date;
  readonly loopIndex?: number;
  readonly resultApplied?: boolean;
  readonly candidateUserId?: Id;
  readonly processDesignId: Id;
  readonly processDesign?: ProcessDesign;
  readonly parentTokenId: Id;
  readonly parentToken?: Token;
  readonly processInstanceHistories?: ProcessInstanceHistory[];
  readonly pendingStateUpdates?: PendingStateUpdate[];
  readonly tokens?: Token[];
  readonly tenantId: Id;
  readonly tenant?: Tenant;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
}

export interface ProcessInstance extends Readonly<ProcessInstanceData> {}
type ProcessInstanceCreateWithChildrenData = Partial<
  Omit<
    ProcessInstanceData,
    'processInstanceHistories' | 'pendingStateUpdates' | 'tokens'
  >
> & {
  processInstanceHistories?: Partial<ProcessInstanceHistory>[];
  pendingStateUpdates?: Partial<PendingStateUpdate>[];
  tokens?: Partial<Token>[];
};

export class ProcessInstance extends BusinessObjectBase {
  static readonly entityName = 'ProcessInstance' as const;
  static readonly plural = 'process-instances';
  static schema = processInstanceSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessInstance[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessInstance(data));
  }

  static async findOne(filter?: any): Promise<ProcessInstance | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessInstance(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ProcessInstance> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessInstance(data);
  }

  static async create(
    data: Omit<ProcessInstanceData, 'id'>,
  ): Promise<ProcessInstance> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessInstance(result);
  }

  static async createMany(
    data: Omit<ProcessInstanceData, 'id'>[],
  ): Promise<ProcessInstance[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessInstance(item));
  }

  static async createWithChildren(
    data: ProcessInstanceCreateWithChildrenData,
  ): Promise<ProcessInstance> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ProcessInstance(result);
  }

  static async createManyWithChildren(
    data: ProcessInstanceCreateWithChildrenData[],
  ): Promise<ProcessInstance[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessInstance(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ProcessInstanceData>,
  ): Promise<ProcessInstance> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ProcessInstance(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async migrate(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/migrate`;
    return BusinessObjectBase.post<any>(url, options);
  }
  static async deliverMessage(options: {
    messageName: string;
    data?: Record<string, unknown>;
  }): Promise<{
    sentTo: Id[];
    started: Id[];
    failedStarts: { designId: Id; name?: string; error: string }[];
  }> {
    const url = `${this.baseUrl}/${this.plural}/deliver-message`;
    return this.post<{
      sentTo: Id[];
      started: Id[];
      failedStarts: { designId: Id; name?: string; error: string }[];
    }>(url, options);
  }
  async pause(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/pause`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async processUpdates(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/process-updates`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async resume(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/resume`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async retry(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/retry`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async sendMessage(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/send-message`;
    return BusinessObjectBase.post<any>(url, options);
  }
  static async startFromName(options: {
    name: string;
    data?: Record<string, unknown>;
    wait?: number;
  }): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/start-from-name`;
    return this.post<any>(url, options);
  }
  static async start(options: {
    designId: Id;
    data?: Record<string, unknown>;
    wait?: number;
    parentTokenId?: Id;
    loopIndex?: number;
    startActivityId?: string;
    message?: Record<string, unknown>;
  }): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/start`;
    return this.post<any>(url, options);
  }
  async updateData(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/update-data`;
    return BusinessObjectBase.post<any>(url, options);
  }
}
